import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BadgeThumb } from 'shared/components/BadgeThumb';
import '../feed/FeedAside.css';
import { ProfileIdentitySearch } from 'features/profiles/ProfileIdentitySearch';
import { RankPrefix } from 'shared/components/RankPrefix';
import { RarityCountsDisplay } from 'shared/components/RarityCountsDisplay';
import { getScoreBreakdown } from 'shared/components/ScoreBreakdownTooltip';
import { useTalosData } from 'shared/context/TalosDataContext';
import { Badge } from 'features/catalog/useBadges';
import { Profile, RawSubmission } from 'features/profiles/useProfiles';
import { LeaderboardExpandState } from 'shared/layout/Sidebar';
import { sortHashBadgesForDisplay } from 'features/catalog/badge-hash';
import { gradientFromHighlightBadges } from 'features/catalog/badge-hash-gradient';
import { JOURNEYS } from 'features/catalog/badge-journeys';
import { FEED_BURST_MS, formatLastUpdated, getFeedUpdatesCutoffDate, talosBadgesLogoURL } from 'config/constants';
import { getFirstPartOfEmail } from 'shared/lib/email';
import { claimPath } from 'shared/lib/nav-paths';
import {
  getProfileSearchSort,
  profileIdsForSearchSort,
  subscribeProfileSearchSort,
} from 'features/profiles/profile-search-sort';
import { expandSheetBadgeIDs, sortScoreForBadgeIDs } from 'features/profiles/profile-score';
import { rankAmongProfilesExcluding } from 'features/profiles/rank-peers';
import {
  compareBadgesByScoreAsc,
  computeJourneyRarityCounts,
  computeRarityCounts,
  getRarityImageUrlByName,
  mergeRarityCounts,
  RARITY_TIERS,
  rarityPoints,
  type RarityCounts,
} from 'features/catalog/rarity';
import '../gallery/BadgeGalleryView.css';
import '../explore/ExploreProfilesView.css';
import './LeaderboardView.css';

interface LeaderboardViewProps {
  /** Logged-in user id — used only to mark "(you)" in the list, not the header picker. */
  ownProfileId: string | null;
  onSelectProfile: (profileId: string) => void;
  onClearProfile?: () => void;
  onSelectBadge?: (badge: Badge) => void;
  /** Highlight claimed badges in the scrolling backdrop while a row is expanded. */
  onBackdropHighlight?: (badgeIds: string[] | null) => void;
  /**
   * Extra ms before the row cascade (boot reveal). Frozen on mount so the animation
   * isn’t already finished under the loading overlay.
   */
  entranceDelayMs?: number;
}

const JOURNEY_NAME_BY_EMOJI = new Map(JOURNEYS.map((journey) => [journey.emoji, journey.name]));

const journeyNameForEmoji = (emoji: string) => JOURNEY_NAME_BY_EMOJI.get(emoji) ?? 'Journey';

const getCoolestBadge = (profile: Profile, badgesMap: Map<string, Badge>): Badge | null =>
  getCoolestBadgeFromIds(profile.badgesIDs, badgesMap);

const getCoolestBadgeFromIds = (badgeIDs: string[], badgesMap: Map<string, Badge>): Badge | null => {
  let coolest: Badge | null = null;
  for (const id of badgeIDs) {
    const badge = badgesMap.get(id);
    if (badge && (!coolest || compareBadgesByScoreAsc(badge, coolest) < 0)) {
      coolest = badge;
    }
  }
  return coolest;
};

/** Lowest badgeScore badges, then rarity order for the Badge Hash–style hover wash. */
const getHashGradientBadges = (profile: Profile, badgesMap: Map<string, Badge>): Badge[] => {
  const top: Badge[] = [];
  const compare = (a: Badge, b: Badge) => compareBadgesByScoreAsc(a, b);

  for (const id of profile.badgesIDs) {
    const badge = badgesMap.get(id);
    if (!badge) continue;
    const insertionIndex = top.findIndex((existing) => compare(badge, existing) < 0);
    if (insertionIndex === -1) {
      if (top.length < 5) top.push(badge);
      continue;
    }
    top.splice(insertionIndex, 0, badge);
    if (top.length > 5) top.pop();
  }
  return sortHashBadgesForDisplay(top);
};

type PersonalClimb = {
  key: string;
  moved: number;
  at: Date;
  fromRank: number;
  toRank: number;
  fromScore: number | null;
  toScore: number;
  /** Coolest badge newly claimed in the submission that caused this climb. */
  bestBadge: Badge | null;
};

/** Fold consecutive climbs in a 12h burst into one card (same window as the badge feed). */
const collapseClimbBurst = (group: PersonalClimb[]): PersonalClimb => {
  if (group.length === 1) return group[0];
  const sorted = [...group].sort((a, b) => a.at.getTime() - b.at.getTime());
  const first = sorted[0];
  const latest = sorted[sorted.length - 1];
  const bestBadge =
    sorted
      .map((climb) => climb.bestBadge)
      .filter((badge): badge is Badge => badge != null)
      .sort(compareBadgesByScoreAsc)[0] ?? null;

  return {
    key: latest.key,
    at: latest.at,
    fromRank: first.fromRank,
    toRank: latest.toRank,
    fromScore: first.fromScore,
    toScore: latest.toScore,
    moved: first.fromRank - latest.toRank,
    bestBadge,
  };
};

/**
 * Merge climbs that fall within 12 hours of the burst's first event into one card
 * (rank/pts span the whole burst; timestamp is the latest climb).
 */
const mergeClimbsInBurstWindow = (climbs: PersonalClimb[]): PersonalClimb[] => {
  if (climbs.length <= 1) return climbs;

  const chronological = [...climbs].sort((a, b) => a.at.getTime() - b.at.getTime());
  const merged: PersonalClimb[] = [];
  let burst: PersonalClimb[] = [];
  let burstStartMs = 0;

  const flush = () => {
    if (burst.length === 0) return;
    merged.push(collapseClimbBurst(burst));
    burst = [];
  };

  for (const climb of chronological) {
    const t = climb.at.getTime();
    if (burst.length === 0) {
      burst = [climb];
      burstStartMs = t;
      continue;
    }
    if (t - burstStartMs < FEED_BURST_MS) {
      burst.push(climb);
    } else {
      flush();
      burst = [climb];
      burstStartMs = t;
    }
  }
  flush();

  return merged;
};

/** Coolest among badges claimed in `current` that were not in `previous`. Falls back to current roster. */
const getCoolestNewClaimBadge = (
  previousBadgeIDs: string[] | null,
  currentBadgeIDs: string[],
  badgesMap: Map<string, Badge>
): Badge | null => {
  if (!previousBadgeIDs) return getCoolestBadgeFromIds(currentBadgeIDs, badgesMap);
  const prevSet = new Set(previousBadgeIDs);
  const newlyClaimed = currentBadgeIDs.filter((id) => !prevSet.has(id));
  return getCoolestBadgeFromIds(newlyClaimed.length > 0 ? newlyClaimed : currentBadgeIDs, badgesMap);
};

const submissionsForProfile = (profile: Profile, submissionsByEmail: Map<string, RawSubmission[]>): RawSubmission[] => {
  const profileId = getFirstPartOfEmail(profile.email);
  const submissions =
    submissionsByEmail.get(profile.email) ??
    [...submissionsByEmail.entries()].find(([email]) => getFirstPartOfEmail(email) === profileId)?.[1] ??
    [];
  return [...submissions].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
};

/** Personal climb lines (consecutive submission peer-ranks) for “X's recent climbs”. */
const buildPersonalClimbsForProfile = (
  profile: Profile,
  submissionsByEmail: Map<string, RawSubmission[]>,
  badgesMap: Map<string, Badge>,
  allRanked: readonly Profile[],
  rankByEmail: Map<string, number>
): PersonalClimb[] => {
  const sorted = submissionsForProfile(profile, submissionsByEmail);
  const cutoff = getFeedUpdatesCutoffDate();
  const climbs: PersonalClimb[] = [];

  if (sorted.length >= 2) {
    for (let i = 1; i < sorted.length; i++) {
      const previous = sorted[i - 1];
      const current = sorted[i];
      if (current.timestamp < cutoff) continue;

      const previousOwned = expandSheetBadgeIDs(previous.badgeIDs, badgesMap, profile);
      const isLatest = i === sorted.length - 1;
      // Latest row is the leaderboard roster (`sortScore` / `badgesIDs`), not a rescore of the paste.
      const currentOwned = isLatest ? profile.badgesIDs : expandSheetBadgeIDs(current.badgeIDs, badgesMap, profile);
      const fromScore = sortScoreForBadgeIDs(previousOwned, badgesMap);
      const toScore = isLatest ? profile.sortScore : sortScoreForBadgeIDs(currentOwned, badgesMap);
      const fromRank = rankAmongProfilesExcluding(allRanked, profile, fromScore, previous.timestamp.getTime());
      const toRank = rankAmongProfilesExcluding(allRanked, profile, toScore, current.timestamp.getTime());
      const moved = fromRank - toRank;
      if (moved <= 0) continue;

      climbs.push({
        key: `${profile.email}-${current.timestamp.getTime()}`,
        moved,
        at: current.timestamp,
        fromRank,
        toRank,
        fromScore,
        toScore,
        bestBadge: getCoolestNewClaimBadge(previousOwned, currentOwned, badgesMap),
      });
    }
  }

  // Fall back to the profile-level climb when consecutive diffs found none
  // (e.g. month-ago baseline used by positionsMovedUp).
  if (climbs.length === 0 && (profile.positionsMovedUp ?? 0) > 0) {
    const previousSubmission = sorted.length >= 2 ? sorted[sorted.length - 2] : null;
    const toRank =
      rankByEmail.get(profile.email) ??
      rankAmongProfilesExcluding(allRanked, profile, profile.sortScore, profile.lastUpdated.getTime());
    const moved = profile.positionsMovedUp!;
    const toScore = profile.sortScore;
    const previousOwned = previousSubmission
      ? expandSheetBadgeIDs(previousSubmission.badgeIDs, badgesMap, profile)
      : null;
    const fromScore = previousOwned ? sortScoreForBadgeIDs(previousOwned, badgesMap) : null;
    climbs.push({
      key: `${profile.email}-current`,
      moved,
      at: profile.lastUpdated,
      fromRank: toRank + moved,
      toRank,
      fromScore,
      toScore,
      bestBadge: getCoolestNewClaimBadge(previousOwned, profile.badgesIDs, badgesMap),
    });
  }

  return mergeClimbsInBurstWindow(climbs)
    .sort((a, b) => b.at.getTime() - a.at.getTime())
    .slice(0, 3);
};

type LeaderboardRowMeta = {
  coolest: Badge | null;
  hashBadges: Badge[];
  rarityCounts: RarityCounts;
  journeyCounts: RarityCounts;
};

const buildLeaderboardRowMeta = (
  profiles: readonly Profile[],
  badgesMap: Map<string, Badge>
): Map<string, LeaderboardRowMeta> => {
  const meta = new Map<string, LeaderboardRowMeta>();
  for (const profile of profiles) {
    const owned = new Set(profile.badgesIDs);
    const journeyCounts = computeJourneyRarityCounts(owned);
    meta.set(profile.email, {
      coolest: getCoolestBadge(profile, badgesMap),
      hashBadges: getHashGradientBadges(profile, badgesMap),
      rarityCounts: mergeRarityCounts(computeRarityCounts(profile.badgesIDs, badgesMap), journeyCounts),
      journeyCounts,
    });
  }
  return meta;
};

export const LeaderboardView: React.FC<LeaderboardViewProps> = ({
  ownProfileId,
  onSelectProfile,
  onClearProfile: _onClearProfile,
  onSelectBadge,
  onBackdropHighlight,
  entranceDelayMs = 0,
}) => {
  const { profilesMap, rankedProfiles, badgesMap, submissionsByEmail } = useTalosData();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedEmail, setExpandedEmail] = useState<string | null>(null);
  const [overflowEmail, setOverflowEmail] = useState<string | null>(null);
  const [searchProfileId, setSearchProfileId] = useState<string | null>(null);
  // Freeze on mount — boot sets a delay so the cascade starts after the overlay lifts.
  const [enterBaseMs] = useState(entranceDelayMs);

  useEffect(() => {
    if (!onBackdropHighlight) return;
    if (!expandedEmail) {
      onBackdropHighlight(null);
      return;
    }
    const profile =
      profilesMap.get(getFirstPartOfEmail(expandedEmail)) ??
      [...profilesMap.values()].find((p) => p.email === expandedEmail) ??
      null;
    onBackdropHighlight(profile?.badgesIDs.length ? [...profile.badgesIDs] : null);
  }, [expandedEmail, profilesMap, onBackdropHighlight]);

  useEffect(() => () => onBackdropHighlight?.(null), [onBackdropHighlight]);

  const allRanked = rankedProfiles;
  const top100 = useMemo(() => allRanked.slice(0, 100), [allRanked]);
  const rankByEmail = useMemo(() => {
    const map = new Map<string, number>();
    allRanked.forEach((profile, index) => map.set(profile.email, index + 1));
    return map;
  }, [allRanked]);

  const displayed = useMemo(() => {
    const rows = top100.map((profile, index) => ({
      profile,
      rank: index + 1,
      overflow: false,
    }));
    if (!overflowEmail) return rows;
    const alreadyListed = top100.some((profile) => profile.email === overflowEmail);
    if (alreadyListed) return rows;
    const overflowProfile = allRanked.find((profile) => profile.email === overflowEmail);
    if (!overflowProfile) return rows;
    const rank = rankByEmail.get(overflowProfile.email) ?? allRanked.length;
    return [...rows, { profile: overflowProfile, rank, overflow: true }];
  }, [top100, overflowEmail, allRanked, rankByEmail]);

  const rowMetaByEmail = useMemo(
    () =>
      buildLeaderboardRowMeta(
        displayed.map(({ profile }) => profile),
        badgesMap
      ),
    [displayed, badgesMap]
  );
  const rowHashGradientByEmail = useMemo(() => {
    const gradients = new Map<string, string>();
    rowMetaByEmail.forEach(({ hashBadges }, email) => {
      if (hashBadges.length > 0) {
        gradients.set(email, gradientFromHighlightBadges(hashBadges));
      }
    });
    return gradients;
  }, [rowMetaByEmail]);

  const ownProfile = ownProfileId ? (profilesMap.get(ownProfileId) ?? null) : null;
  const climbsFocusId = searchProfileId ?? ownProfileId;
  const climbsProfile = climbsFocusId ? (profilesMap.get(climbsFocusId) ?? null) : null;
  const isOwnClimbs = !!climbsProfile && !!ownProfile && climbsProfile.email === ownProfile.email;

  const focusedRecentClimbs = useMemo((): PersonalClimb[] => {
    if (!climbsProfile) return [];
    const climbs = buildPersonalClimbsForProfile(climbsProfile, submissionsByEmail, badgesMap, allRanked, rankByEmail);
    if (climbs.length === 0) return climbs;
    // Top line matches the leaderboard: coolest roster badge, not the climb delta badge.
    const coolest = getCoolestBadge(climbsProfile, badgesMap);
    return [{ ...climbs[0], bestBadge: coolest }, ...climbs.slice(1)];
  }, [climbsProfile, submissionsByEmail, badgesMap, allRanked, rankByEmail]);

  const climbsSectionTitle =
    isOwnClimbs || !climbsProfile ? 'Your Recent Climbs' : `${climbsProfile.name}'s recent climbs`;

  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const pendingScrollEmailRef = useRef<string | null>(null);

  const openAndScrollToProfile = useCallback((email: string) => {
    pendingScrollEmailRef.current = email;
    setExpandedEmail(email);
  }, []);

  const focusOnLeaderboard = useCallback(
    (profileId: string) => {
      const profile = profilesMap.get(profileId);
      if (!profile) return;
      const rank = rankByEmail.get(profile.email) ?? Number.POSITIVE_INFINITY;
      setSearchProfileId(profileId);
      setOverflowEmail(rank > 100 ? profile.email : null);
      openAndScrollToProfile(profile.email);
    },
    [profilesMap, rankByEmail, openAndScrollToProfile]
  );

  // Sidebar rank/pts (and similar) navigate here with expandProfileId in location state.
  useEffect(() => {
    const focusId = (location.state as LeaderboardExpandState | null)?.expandProfileId;
    if (!focusId || !profilesMap.has(focusId)) return;
    focusOnLeaderboard(focusId);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, profilesMap, focusOnLeaderboard, navigate]);

  const clearLeaderboardFocus = () => {
    setSearchProfileId(null);
    setOverflowEmail(null);
    setExpandedEmail(null);
  };

  const [profileSort, setProfileSort] = useState(() => getProfileSearchSort());
  useEffect(() => subscribeProfileSearchSort(() => setProfileSort(getProfileSearchSort())), []);

  const cycleIds = useMemo(
    () => profileIdsForSearchSort(profilesMap.values(), profileSort, rankedProfiles),
    [profilesMap, profileSort, rankedProfiles]
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (cycleIds.length === 0) return;

      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }

      e.preventDefault();
      const anchorId = searchProfileId ?? ownProfileId;
      const currentIndex = anchorId ? cycleIds.indexOf(anchorId) : -1;
      let nextIndex: number;
      if (e.key === 'ArrowLeft') {
        nextIndex = currentIndex <= 0 ? cycleIds.length - 1 : currentIndex - 1;
      } else if (currentIndex < 0 || currentIndex >= cycleIds.length - 1) {
        nextIndex = 0;
      } else {
        nextIndex = currentIndex + 1;
      }
      focusOnLeaderboard(cycleIds[nextIndex]);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [cycleIds, searchProfileId, ownProfileId, focusOnLeaderboard]);

  useEffect(() => {
    const email = pendingScrollEmailRef.current;
    if (!email || expandedEmail !== email) return;
    const timer = window.setTimeout(() => {
      const node = rowRefs.current.get(email);
      if (!node) return;
      pendingScrollEmailRef.current = null;
      node.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [expandedEmail, displayed]);

  return (
    <div className="lb-layout">
      <header className="lb-layout-header gallery-header">
        <ProfileIdentitySearch
          selectedProfileId={searchProfileId}
          onSelectProfile={focusOnLeaderboard}
          onClear={clearLeaderboardFocus}
          avatarSrc={talosBadgesLogoURL}
        >
          <h1 className="gallery-title">Leaderboard</h1>
          <p className="gallery-subtitle">Top #100 ranked by badge score + journey points</p>
        </ProfileIdentitySearch>
      </header>

      <div className="lb-main">
        <div className="lb-list">
          {displayed.map(({ profile, rank, overflow }, index) => {
            const id = getFirstPartOfEmail(profile.email);
            const isYou = ownProfileId === id;
            const meta = rowMetaByEmail.get(profile.email);
            const coolestBadge = meta?.coolest ?? null;
            const hashGradient = rowHashGradientByEmail.get(profile.email);
            const journeys = profile.journeyEmojis;
            const topEmoji = journeys.at(0) ?? '';
            const isExpanded = expandedEmail === profile.email;
            const breakdown = isExpanded ? getScoreBreakdown(profile.email, profilesMap, badgesMap) : null;
            const rarityCounts =
              meta?.rarityCounts ??
              mergeRarityCounts(
                computeRarityCounts(profile.badgesIDs, badgesMap),
                computeJourneyRarityCounts(profile.badgesIDs)
              );
            const journeyCounts = meta?.journeyCounts ?? computeJourneyRarityCounts(profile.badgesIDs);
            // First 10 keep a readable cascade; after that hurry (and cap) so deep links aren't stuck waiting.
            const cascadeMs = index < 10 ? index * 55 : Math.min(10 * 55 + (index - 10) * 8, 700);
            const enterDelay = `${enterBaseMs + cascadeMs}ms`;

            return (
              <React.Fragment key={profile.email}>
                {overflow ? (
                  <p className="lb-overflow-note" role="note">
                    Outside top 100
                  </p>
                ) : null}
                <div
                  ref={(el) => {
                    if (el) rowRefs.current.set(profile.email, el);
                    else rowRefs.current.delete(profile.email);
                  }}
                  className={`lb-item ${isYou ? 'you' : ''} ${isExpanded ? 'expanded' : ''}${
                    overflow ? ' lb-item--overflow' : ''
                  }${rank <= 3 && !overflow ? ' lb-item--podium' : ''}${hashGradient ? ' lb-item--hash' : ''}`}
                  style={
                    {
                      '--lb-enter-delay': enterDelay,
                      ...(hashGradient ? { '--lb-hash-gradient': hashGradient } : {}),
                    } as React.CSSProperties
                  }
                >
                  <div className="lb-row">
                    <div className="lb-avatar-rail">
                      {coolestBadge ? (
                        <button
                          type="button"
                          className="lb-hash-cell"
                          title={coolestBadge.name}
                          aria-label={`Open ${coolestBadge.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectBadge?.(coolestBadge);
                          }}
                        >
                          <BadgeThumb badge={coolestBadge} size="md" alt="" />
                        </button>
                      ) : (
                        <div className="lb-avatar lb-avatar--empty" aria-hidden />
                      )}
                    </div>
                    <div
                      className="lb-row-main"
                      role="button"
                      tabIndex={0}
                      aria-expanded={isExpanded}
                      onClick={() => {
                        if (isExpanded) clearLeaderboardFocus();
                        else focusOnLeaderboard(id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (isExpanded) clearLeaderboardFocus();
                          else focusOnLeaderboard(id);
                        }
                      }}
                    >
                      <div className="explore-option lb-option">
                        <div className="lb-profile-side">
                          <span className="explore-option-identity">
                            <RankPrefix rank={rank} />{' '}
                            <button
                              type="button"
                              className="lb-name-link"
                              onClick={(e) => {
                                e.stopPropagation();
                                onSelectProfile(id);
                              }}
                            >
                              {profile.name}
                            </button>
                            {isYou ? ' (you)' : ''}
                          </span>
                          <span className="explore-option-updated">
                            last updated: {formatLastUpdated(profile.lastActivityAt)}
                          </span>
                        </div>
                        <div className="lb-stats-side">
                          <span className="explore-option-score">
                            <i>{profile.sortScore}</i>
                            {topEmoji ? (
                              <span className="explore-option-emoji" title={journeyNameForEmoji(topEmoji)}>
                                {topEmoji}
                              </span>
                            ) : null}
                          </span>
                          <span className="explore-option-rarities">
                            <RarityCountsDisplay
                              counts={rarityCounts}
                              itemClassName="rarity-count-dropdown-item"
                              iconStyle={{
                                width: rank <= 3 && !overflow ? '16px' : '14px',
                                height: rank <= 3 && !overflow ? '16px' : '14px',
                              }}
                            />
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={`lb-chevron ${isExpanded ? 'open' : ''}`}
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? 'Hide score breakdown' : 'Show score breakdown'}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isExpanded) clearLeaderboardFocus();
                        else focusOnLeaderboard(id);
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                      >
                        <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>

                  {isExpanded && breakdown ? (
                    <div className="lb-breakdown">
                      <div className="lb-breakdown-header">
                        <span className="lb-breakdown-title">Score breakdown</span>
                        <span className="lb-breakdown-total">{breakdown.sortScore} pts</span>
                      </div>

                      {RARITY_TIERS.some(({ key }) => breakdown.badgeCounts[key] > 0) ? (
                        <div className="lb-breakdown-section">
                          <p className="lb-breakdown-section-label">Badges</p>
                          {RARITY_TIERS.map(({ key, score, label }) => {
                            const count = breakdown.badgeCounts[key];
                            if (count === 0) return null;
                            return (
                              <div key={key} className="lb-breakdown-row">
                                <span className="lb-breakdown-left">
                                  <img src={getRarityImageUrlByName(key)} alt="" className="lb-breakdown-icon" />
                                  <span>
                                    {count} {label} × {score}
                                  </span>
                                </span>
                                <span className="lb-breakdown-leader" aria-hidden />
                                <span className="lb-breakdown-pts">+{count * score} pts</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}

                      {RARITY_TIERS.some(({ key }) => journeyCounts[key] > 0) ? (
                        <div className="lb-breakdown-section">
                          <p className="lb-breakdown-section-label">Journeys</p>
                          {RARITY_TIERS.map(({ key, score, label }) => {
                            const count = journeyCounts[key];
                            if (count === 0) return null;
                            return (
                              <div key={key} className="lb-breakdown-row">
                                <span className="lb-breakdown-left">
                                  <img src={getRarityImageUrlByName(key)} alt="" className="lb-breakdown-icon" />
                                  <span>
                                    {count} {label} × {score}
                                  </span>
                                </span>
                                <span className="lb-breakdown-leader" aria-hidden />
                                <span className="lb-breakdown-pts">+{count * score} pts</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {rank === 3 && !overflow ? <div className="lb-podium-gap" aria-hidden /> : null}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <aside className="feed-aside lb-aside">
        <section className="feed-aside-card lb-scoring-card">
          <div className="feed-aside-card-head">
            <h2 className="feed-aside-title">How scoring works</h2>
          </div>
          <div className="lb-scoring-body">
            <p>
              Each badge adds points by rarity:
              <br />
              <span className="lb-rarity-word rarity-mythic">Mythic</span> <strong>{rarityPoints.mythic}</strong> ·{' '}
              <span className="lb-rarity-word rarity-legendary">Legendary</span>{' '}
              <strong>{rarityPoints.legendary}</strong> · <span className="lb-rarity-word rarity-rare">Rare</span>{' '}
              <strong>{rarityPoints.rare}</strong> · <span className="lb-rarity-word rarity-common">Common</span>{' '}
              <strong>{rarityPoints.common}</strong>.
            </p>
            <p>Completing a journey adds that journey’s rarity points as a bonus.</p>
          </div>
        </section>

        <section className="feed-aside-card">
          <div className="feed-aside-card-head">
            <h2 className="feed-aside-title">{climbsSectionTitle}</h2>
            {isOwnClimbs && ownProfileId ? (
              <button type="button" className="feed-aside-link" onClick={() => navigate(claimPath(ownProfileId))}>
                Claim New Badges
              </button>
            ) : null}
          </div>
          {!climbsProfile ? (
            <p className="feed-aside-empty">Create a profile to see your climbs here.</p>
          ) : focusedRecentClimbs.length === 0 ? (
            <p className="feed-aside-empty">
              {isOwnClimbs ? 'No recent climbs of yours.' : `No recent climbs for ${climbsProfile.name}.`}
            </p>
          ) : (
            <ul className="feed-aside-activity lb-climbs-list">
              {focusedRecentClimbs.map((climb) => {
                const coolest = climb.bestBadge;
                return (
                  <li key={climb.key}>
                    <button
                      type="button"
                      className="feed-aside-activity-item lb-climb-item"
                      onClick={() => openAndScrollToProfile(climbsProfile.email)}
                    >
                      {coolest ? (
                        <img src={coolest.smallImageUrl} alt="" className="feed-aside-activity-badge" />
                      ) : (
                        <div className="feed-aside-activity-badge feed-aside-activity-badge--empty" aria-hidden />
                      )}
                      <div className="feed-aside-activity-text lb-climb-text">
                        <p className="lb-climb-stats">
                          <span className="lb-climb-stat">
                            <span className="lb-climb-stat-label">Rank</span>
                            <span className="lb-climb-stat-value">
                              #{climb.fromRank}
                              <span className="lb-climb-arrow" aria-hidden>
                                →
                              </span>
                              #{climb.toRank}
                            </span>
                          </span>
                          <span className="lb-climb-stat">
                            <span className="lb-climb-stat-label">Pts</span>
                            <span className="lb-climb-stat-value">
                              {climb.fromScore != null ? (
                                <>
                                  {climb.fromScore}
                                  <span className="lb-climb-arrow" aria-hidden>
                                    →
                                  </span>
                                </>
                              ) : null}
                              {climb.toScore}
                            </span>
                          </span>
                        </p>
                        <p className="feed-aside-activity-time">
                          <span className="lb-climb-delta">↑ {climb.moved}</span>
                          <span className="lb-climb-time-sep" aria-hidden>
                            ·
                          </span>
                          {formatLastUpdated(climb.at)}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </aside>
    </div>
  );
};
